import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getRichContentMessageForTextingStep } from '../../../../../models/UiUtils';
import { bs } from '../../../../styles/styles';
import { Checkbox } from '../../../../components/Checkbox';
import { Collapsible } from '../../../../components/Collapsible';
import { InputFieldError } from '../../../../components/InputFieldError';
import { TextInput } from '../../../../components/TextInput';
import { AutomationTagAutocomplete } from '../../../../components/automation/AutomationTriggerCard/presentation';
import { EditAutomationStepEmailComposerModal } from '../../../../components/automation/EditAutomationStepEmailComposerModal';
import { AdvancedOptionsButton } from '../../../../components/automation/AutomationStepCard/presentation';
import { StepSendFromOption } from '../../../../components/automation/AutomationStepCard/components/StepSendFromOption';
import { styleSheet } from '../../../../components/automation/AutomationStepCard/styles';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useEmailStepSave } from '../../hooks/useEmailStepSave';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { ConditionalFilter } from '../../components/ConditionalFilter';
import type { AutomationStepNodeData } from '../../types/stepNodes';

interface EmailStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function EmailStepRenderer({ nodeId, data, readOnly: readOnlyProp }: EmailStepRendererProps) {
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();

	const { automationStep, automationTemplate, stepData } = data;
	const emailStep = automationStep as Api.IEmailAutomationStep;
	const template = automationTemplate;

	const trigger = template?.draft?.trigger || template?.published?.trigger;

	const [name, setName] = React.useState(emailStep?.name || stepData?.name || '');
	const [showEmailEditor, setShowEmailEditor] = React.useState(false);
	const [advancedOptionsToggled, setAdvancedOptionsToggled] = React.useState(() => {
		if (emailStep?.tagToAddOnClick || emailStep?.options?.sendToSender) {
			return true;
		}
		return false;
	});
	const [tagToAddOnClick, setTagToAddOnClick] = React.useState(emailStep?.tagToAddOnClick || '');
	const [showEmailLinkClickTags, setShowEmailLinkClickTags] = React.useState(!!emailStep?.tagToAddOnClick);
	const [skipStepContactFilter, setSkipStepContactFilter] = React.useState(emailStep?.contactFilter);
	const [showRequiredFieldError, setShowRequiredFieldError] = React.useState(false);

	const canEdit = template?.status !== 'Archived' && !readOnlyProp;
	const readonly = !canEdit || readOnlyProp;

	const autoSave = useAutoSave({
		templateId: template?.id,
		enabled: !readonly,
		onSaveSuccess: () => {
			// Query invalidation happens automatically
		},
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const clearInlineErrorMessage = () => {
		setShowRequiredFieldError(false);
	};

	const { save } = useEmailStepSave({
		emailStep,
		stepId: data.stepId,
		templateId: template?.id,
		name,
		tagToAddOnClick,
		skipStepContactFilter,
		autoSave,
	});

	const onNameInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setName(e.target.value);
		clearInlineErrorMessage();
	};

	const onEmailCtaClicked = () => {
		setShowEmailEditor(true);
		clearInlineErrorMessage();
	};

	const onTagForLinkClickChanged = (tag: string) => {
		setTagToAddOnClick(tag);
		save({ newTagToAddOnClick: tag });
	};

	const onAddTagToLinkClickCheckChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setShowEmailLinkClickTags(checked);
		if (!checked) {
			setTagToAddOnClick('');
			save({ newTagToAddOnClick: '' });
		}
	};

	const onEmailComposerModalRequestClose = () => {
		setShowEmailEditor(false);
		// Query invalidation will handle refreshing the data
		// The parent component manages the actual step data
	};

	const saveSenderOptions = (option: Api.ISendFromOptions, sendToSender: boolean) => {
		save({ newSendFrom: option, newSendToSender: sendToSender });
	};

	const handleFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
	};

	const hasEmailContent = !!emailStep?.content;

	let errorMessage = null;
	if (showRequiredFieldError) {
		const hasName = !!name;
		if (!hasName || !hasEmailContent) {
			errorMessage = 'Please provide email content and description/name.';
		}
	}

	return (
		<>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='detailed'
				lastSavedAt={autoSave.lastSavedAt}
				errorMessage={autoSave.errorMessage}
			/>
			<div className={css(styleSheet.fieldLabel)}>
				Email title: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<TextInput
				className={css(styleSheet.emailNameInput)}
				disabled={!canEdit}
				inputId={`automation-email-step-name-input-${emailStep?.id || nodeId}`}
				key={`${nodeId}-name-input`}
				onBlur={() => save()}
				onChange={onNameInputChanged}
				onFocus={clearInlineErrorMessage}
				placeholder='Enter a brief description of this email'
				type='text'
				value={name || ''}
			/>
			<InputFieldError errorMessage={errorMessage}>
				<button
					className={css(
						styleSheet.emailCta,
						!hasEmailContent ? styleSheet.emailCreateButton : styleSheet.emailEditButton
					)}
					disabled={readonly}
					onClick={onEmailCtaClicked}
				>
					<span>{`${hasEmailContent ? 'Preview / Edit' : 'Create'} Email`}</span>
				</button>
			</InputFieldError>
			<AdvancedOptionsButton
				isCollapsed={!advancedOptionsToggled}
				onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
			/>
			<Collapsible isCollapsed={!advancedOptionsToggled}>
				<div className={css(styleSheet.advancedOptionsContent)}>
					<Checkbox
						className={css(styleSheet.addTagOnLinkClickCheckbox)}
						id={emailStep?.id ? `automation-add-tag-on-link-click-checkbox-${emailStep.id}` : uuid()}
						checked={showEmailLinkClickTags}
						type='large'
						disabled={readonly}
						onChange={onAddTagToLinkClickCheckChanged}
					>
						<div>
							<div className={css(styleSheet.checkboxText)}>
								Add a tag if the recipient clicks (any) link in the email
							</div>
						</div>
					</Checkbox>
					{showEmailLinkClickTags ? (
						<AutomationTagAutocomplete
							className={css(styleSheet.tagAutomcomplete)}
							limit={5}
							onTagSelected={onTagForLinkClickChanged}
							initialTag={tagToAddOnClick}
						/>
					) : null}
					{emailStep && (
						<StepSendFromOption
							showInternalSendFromOption
							step={{
								automationStep: emailStep as unknown as Api.AutomationStepViewModel,
								uuid: emailStep.id || nodeId,
							}}
							onSave={saveSenderOptions}
							connectionTypes={[]}
							canEdit={canEdit}
						/>
					)}
					<ConditionalFilter
						stepId={data.stepId || nodeId}
						filter={skipStepContactFilter}
						canEdit={canEdit}
						isBusy={false}
						onFilterChange={handleFilterChange}
					/>
				</div>
			</Collapsible>
			{showEmailEditor && emailStep && template ? (
				<EditAutomationStepEmailComposerModal
					automationTemplateId={template.id}
					step={emailStep}
					impersonationContext={undefined}
					onRequestClose={onEmailComposerModalRequestClose}
					onStepChanged={() => {
						// Query invalidation will handle refreshing the data
					}}
					onAttachmentRemoved={() => {
						// Query invalidation will handle refreshing the data
					}}
					readonly={readonly}
					trigger={trigger}
					defaultInitialContent={Api.createRawRichTextContentStateWithText(
						getRichContentMessageForTextingStep(userSession, trigger, emailStep.schedule)
					)}
				/>
			) : null}
		</>
	);
}
